import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { completeOnboarding, getOnboardingStatus, type OnboardingStatus } from "@/api/onboarding";
import { useAuthStore } from "@/store/authStore";

export const onboardingStatusQueryKey = (userId?: string) => ["onboardingStatus", userId ?? "anonymous"] as const;

const pendingCompletionKey = (userId: string) => `tobo:onboarding-completion-pending:${userId}`;

async function getStatusAndRetryPendingCompletion(userId: string): Promise<OnboardingStatus> {
  const status = await getOnboardingStatus(userId);

  if (status.isCompleted || localStorage.getItem(pendingCompletionKey(userId)) !== "true") {
    return status;
  }

  try {
    const completedAt = await completeOnboarding(userId);
    localStorage.removeItem(pendingCompletionKey(userId));

    return { completedAt, isCompleted: true };
  } catch {
    return status;
  }
}

export function useOnboardingStatusQuery(enabled = true) {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery({
    queryKey: onboardingStatusQueryKey(userId),
    queryFn: () => getStatusAndRetryPendingCompletion(userId!),
    enabled: enabled && Boolean(userId),
    staleTime: 5 * 60 * 1000,
  });
}

/** 저장 실패 시에도 현재 세션은 통과시키고, 다음 실행에서 서버 저장을 재시도한다. */
export function useCompleteOnboardingMutation() {
  const userId = useAuthStore(state => state.user?.id);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => {
      if (!userId) {
        throw new Error("로그인이 필요합니다.");
      }

      return completeOnboarding(userId);
    },
    onMutate: () => {
      if (!userId) {
        return;
      }

      queryClient.setQueryData<OnboardingStatus>(onboardingStatusQueryKey(userId), {
        completedAt: new Date().toISOString(),
        isCompleted: true,
      });
    },
    onSuccess: completedAt => {
      if (!userId) {
        return;
      }

      localStorage.removeItem(pendingCompletionKey(userId));
      queryClient.setQueryData<OnboardingStatus>(onboardingStatusQueryKey(userId), {
        completedAt,
        isCompleted: true,
      });
    },
    onError: () => {
      if (userId) {
        localStorage.setItem(pendingCompletionKey(userId), "true");
      }
    },
  });
}
